import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { IconKebab } from 'svgIcons/MotionPortalIcons';
import { MENU_WIDTH } from './constants';
import { getMenuPosition, isButtonVisibleInContainer } from './helpers';
import './style.scss';

const ContextMenuComponent = (props) => {
    const wrapperRef = useRef(null);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const isOpen = props.open;
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    const handleToggle = () => {
        if (props.onToggle) {
            props.onToggle(!isOpen);
        }
    };

    const handleSelection = (option) => {
        if (isOpen && props.onClose) {
            props.onClose();
        }
        const callback = option.onOptionSelect || props.onSelect;
        if (typeof callback === 'function') {
            callback(option.id);
        }
    };

    useEffect(() => {
        if (!isOpen) { return; }

        const handleOutsideClick = (evt) => {
            const menuNode = menuRef.current;
            const buttonNode = buttonRef.current;
            if (
                menuNode && menuNode.contains(evt.target) ||
                buttonNode && buttonNode.contains(evt.target)
            ) {
                return;
            }
            if (isOpen && props.onClose) {
                props.onClose();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [isOpen, props.onClose]);

    // Position menu and handle all relevant events
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setIsPositioned(false);

        const positionMenu = () => {
            const pos = getMenuPosition({ buttonRef, menuRef, menuWidth: MENU_WIDTH });
            setMenuPosition(pos);
            setIsPositioned(true);
        };

        positionMenu();

        // Reposition on scroll/resize
        window.addEventListener('scroll', positionMenu, true);
        window.addEventListener('resize', positionMenu);

        // Hide menu if button is out of scrollable area
        const scrollContainer = document.querySelector('.drives-list__body');
        const handleScroll = () => {
            if (!isButtonVisibleInContainer(buttonRef, scrollContainer) && props.onClose) {
                props.onClose();
            } else {
                positionMenu();
            }
        };
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
        }

        return () => {
            window.removeEventListener('scroll', positionMenu, true);
            window.removeEventListener('resize', positionMenu);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isOpen, props.options.length, props.onClose]);

    const buttonClass = isOpen ? 'context-menu-button-active' : 'context-menu-button';

    return (
        <div className='context-menu' ref={wrapperRef} role='context-menu'>
            <button
                id={props.id}
                className={buttonClass}
                ref={buttonRef}
                onClick={handleToggle}
                type='button'
                aria-haspopup='menu'
                aria-expanded={isOpen}
            >
                <IconKebab active={isOpen} />
            </button>
            {isOpen && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className='context-menu-list'
                    style={{
                        position: 'absolute',
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`,
                        zIndex: 9999,
                        visibility: isPositioned ? 'visible' : 'hidden',
                    }}
                >
                    {props.options.map((option, index) =>
                        <div
                            id={`contextMenuItem_${option.id}_${index}`}
                            key={`contextMenuItem_${option.id}_${index}`}
                            className={option.disabled ? 'context-menu-list-item-disabled' : 'context-menu-list-item'}
                            role='presentation'
                            onClick={() => {
                                if (!option.disabled) { handleSelection(option); }
                            }}
                        >
                            {option.title}
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

ContextMenuComponent.defaultProps = {
    id: '',
    options: [],
    onSelect: undefined,
};

ContextMenuComponent.propTypes = {
    id: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            title: PropTypes.node.isRequired,
            disabled: PropTypes.bool,
            onOptionSelect: PropTypes.func,
        })
    ),
    onSelect: PropTypes.func,
    onToggle: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
};

export default ContextMenuComponent;
